<template>
  <v-row justify="center">
    <v-col cols="12" lg="10" class="admin-container">
      <!-- Header with Add Numbers Button -->
      <div class="header-actions">
        <v-btn color="primary" class="add-numbers-btn" elevation="2" @click="numbersDialog = true">
          <v-icon left>mdi-phone-plus</v-icon>
          הוסף מספרים
        </v-btn>

        <v-btn class="export-btn" elevation="2" :loading="exportProgress" :disabled="exportProgress"
          @click="exportNumbersToCsv">
          <v-icon left>mdi-file-delimited-outline</v-icon>
          ייצוא ל-CSV
        </v-btn>
      </div>

      <!-- Search and Stats Card -->
      <v-card class="search-card" elevation="2">
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" md="4" sm="6">
              <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="חיפוש מספר" outlined dense
                hide-details background-color="white" class="search-field" @input="handleSearchInput"></v-text-field>
            </v-col>
            <v-col cols="12" md="8" sm="6" class="d-flex align-center">
              <div class="numbers-count">
                <v-icon color="#0d2c6d" class="ml-2">mdi-phone</v-icon>
                <span>
                  סך הכל
                  <strong>{{ totalItems.toLocaleString() }}</strong>
                  מספרים
                </span>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Numbers Data Table Card -->
      <v-card class="data-table-card" elevation="2">
        <v-data-table id="table1" :headers="headers" :items="numbers" class="numbers-table" :loading="progressShow"
          loading-text="טוען נתונים..." no-data-text="אין עדיין מספרים" :server-items-length="totalItems"
          :items-per-page="itemsPerPage" :page.sync="currentPage" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc"  must-sort
          @update:items-per-page="onItemsPerPageChange" @update:page="onPageChange" @update:sort-by="onSortChange"
          @update:sort-desc="onSortChange" :footer-props="{
            'items-per-page-options': [20, 50, 100, 500], 'items-per-page-text': 'שורות בעמוד:', showFirstLastPage: true,
            firstIcon: 'mdi-chevron-double-left',
            lastIcon: 'mdi-chevron-double-right',
            prevIcon: 'mdi-chevron-left',
            nextIcon: 'mdi-chevron-right'
          }">
          <!-- Custom Headers -->
          <template v-for="header in headers" v-slot:[`header.${header.value}`]="{ header }">
            <span class="table-header-text">{{ header.text }}</span>
          </template>

          <!-- Desktop rows -->
          <template v-if="$vuetify.breakpoint.mdAndUp" #item="{ item, index }">
            <tr class="table-row">
              <td class="table-cell">
                {{ (currentPage - 1) * itemsPerPage + index + 1 }}
              </td>
              <td class="table-cell">{{ item.line_number }}</td>
              <td class="table-cell">
                <v-chip small :color="getStatusColor(item.status)" text-color="white">
                  {{ item.status }}
                </v-chip>
              </td>
              <td class="table-cell">
                <v-chip small color="#6a6aec" text-color="white">
                  {{ item.type }}
                </v-chip>
              </td>
              <td class="table-cell">{{ item.belong_to_agent || 'לא משויך' }}</td>
              <td class="table-cell">
                <div class="action-buttons">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn icon small v-on="on" color="#0d2c6d" @click="openEditDialog(item, index)">
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>עריכה</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn icon small v-on="on" color="#d32f2f" @click="opendeleteDialog(item, index)">
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>מחיקה</span>
                  </v-tooltip>
                </div>
              </td>
            </tr>
          </template>

          <!-- Mobile display (actions only) -->
          <template v-slot:item.actions="{ item, index }">
            <div class="action-buttons">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn icon small v-on="on" color="#0d2c6d" @click="openEditDialog(item, index)">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>עריכה</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn icon small v-on="on" color="#d32f2f" @click="opendeleteDialog(item, index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>מחיקה</span>
              </v-tooltip>
            </div>
          </template>

          <!-- No Data Template -->
          <template v-slot:no-data>
            <v-row v-if="progressShow" class="my-5">
              <v-col class="d-flex justify-center">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </v-col>
            </v-row>
            <div v-else class="empty-state">
              <v-icon size="64" color="#0d2c6d">mdi-phone-off</v-icon>
              <span class="empty-text">אין עדיין מספרים</span>
              <v-btn color="primary" @click="refreshDetails()" class="mt-4">
                <v-icon left>mdi-refresh</v-icon>
                רענן
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <!-- Dialogs -->
      <AddNumbersDialog v-model="numbersDialog" v-if="numbersDialog"></AddNumbersDialog>

      <EditNumberDialog v-model="editNumberDialog" v-if="editNumberDialog" :editNumber="editNumber"
        @numberAgentChange="editedNumberSaved"></EditNumberDialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="deleteDialog" max-width="420" persistent>
        <v-card class="delete-dialog-card">
          <v-card-title class="delete-dialog-title">
            <v-icon color="#d32f2f" class="ml-2">mdi-alert-circle-outline</v-icon>
            מחיקת מספר
          </v-card-title>

          <v-card-text class="delete-dialog-text">
            האם אתה בטוח שברצונך למחוק את המספר
            <strong>{{ itemToDelete.line_number }}</strong>?
            <br />
            פעולה זו אינה ניתנת לשחזור.
          </v-card-text>

          <v-card-actions class="delete-dialog-actions">
            <v-spacer></v-spacer>
            <v-btn text color="#0d2c6d" :disabled="deleteProgress" @click="closeDeleteDialog">
              ביטול
            </v-btn>
            <v-btn color="#d32f2f" dark :loading="deleteProgress" @click="deleteNumber">
              <v-icon left>mdi-delete</v-icon>
              מחק
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>
    </v-col>
  </v-row>
</template>


<script>
import ApiServices from '@/services/api.service'
import SnackBar from '@/components/widgets/snackBar.vue'
import Auth from '@/services/auth.service.js'
import EditNumberDialog from '@/components/admin/dialogs/EditNumberDialog'
import AddNumbersDialog from '@/components/admin/dialogs/AddNumbersDialog'
import _ from 'lodash'

export default {
  components: {
    SnackBar,
    AddNumbersDialog,
    EditNumberDialog
  },
  data: () => ({
    snackbar: false,
    snackbarColorBt: "green",
    snacbarText: "",
    lineNumber: "",
    progressShow: false,
    numbersDialog: false,
    editNumberDialog: false,
    editNumber: {},
    importDialog: false,
    importMessageDialog: false,
    importedFailed: [],
    importedSuccess: [],
    formTitle: "",
    itemToDelete: {},
    deleteDialog: false,
    deleteProgress: false,
    exportProgress: false,
    search: "",
    numbers: [],

    // === סרבר פייגינג ===
    totalItems: 0,
    currentPage: 1,
    itemsPerPage: 20,
    sortBy: 'createdAt',
    sortDesc: true,

    headers: [
      { text: '#', value: "index", sortable: false, width: '50px' },
      { text: 'מספר', value: 'line_number', align: 'center' },
      { text: 'מצב', value: 'status', width: '150px', align: 'center' },
      { text: 'סוג', value: 'type', width: '120px', align: 'center' },
      { text: 'משוייך לסוכן', value: 'belong_to_agent', align: 'center' },
      { text: 'פעולות', value: 'actions', sortable: false, width: '100px' },
    ],
  }),
  methods: {
    getStatusColor(status) {
      if (status.includes('מחובר')) {
        return '#4caf50'; // ירוק – מחובר
      } else {
        return '#ff9800'; // כתום – פנוי
      }
    },

    async getNumbers(newSearch = false) {
      try {
        if (newSearch) {
          this.currentPage = 1;
        }

        this.progressShow = true;
        const token = localStorage.getItem("token");

        const query = new URLSearchParams({
          page: this.currentPage,
          itemsPerPage: this.itemsPerPage,
          sortBy: this.sortBy || '',
          sortDesc: this.sortDesc ? 'desc' : 'asc',
          search: this.search || ''
        }).toString();

        const api = process.env.VUE_APP_BASE_URL + `/admin_api/get_numbers_list?${query}`;
        const res = await fetch(api, ApiServices.requestOptions("GET", "", token));
        const jsonObject = await res.json();

        if (res.status >= 400) {
          this.showSnackBar("שגיאה בקבלת נתונים", "red");
        } else if (res.status === 200) {
          // מצופה פורמט: { data: [...], totalItems: number }
          this.numbers = jsonObject.data || [];
          this.totalItems = jsonObject.totalItems || 0;
          this.formatDetails();
        }
      } catch (error) {
        this.showSnackBar("Error get numbers: " + error, "red");
      } finally {
        this.progressShow = false;
      }
    },

    formatDetails() {
      this.numbers = this.numbers.map((number) => {
        const statusValue = number.user_active
          ? `מחובר למשתמש ${number.user_active}`
          : 'פנוי';

        return {
          ...number,
          status: statusValue,
        };
      });
    },

    showSnackBar(message, color) {
      this.snacbarText = message;
      this.snackbarColorBt = color;
      this.snackbar = true;
    },

    openEditDialog(item, index) {
      this.editNumber = item;
      this.editNumberDialog = true;
    },

    editedNumberSaved(item, text, color) {
      this.showSnackBar(text, color);
      if (!item || item === "") {
        return;
      }
      const index = this.numbers.findIndex(number => number._id === item._id);
      if (index !== -1) {
        Object.assign(this.numbers[index], item);
      }
    },

    opendeleteDialog(item, index) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },

    closeDeleteDialog() {
      this.deleteDialog = false;
      this.itemToDelete = {};
    },

    async deleteNumber() {
      try {
        this.deleteProgress = true;
        const token = localStorage.getItem("token");

        const body = JSON.stringify({
          _id: this.itemToDelete._id,
          line_number: this.itemToDelete.line_number,
        });

        const api = process.env.VUE_APP_BASE_URL + "/admin_api/delete_number";
        const res = await fetch(api, ApiServices.requestOptions("DELETE", body, token));
        const jsonObject = await res.json();

        if (res.status >= 400) {
          this.showSnackBar(jsonObject.message || "שגיאה במחיקת המספר", "red");
        } else if (res.status === 200) {
          // הסרה מקומית מהטבלה
          const index = this.numbers.findIndex(number => number._id === this.itemToDelete._id);
          if (index !== -1) {
            this.numbers.splice(index, 1);
            this.totalItems = Math.max(0, this.totalItems - 1);
          }
          this.showSnackBar("המספר נמחק בהצלחה", "green");
          this.closeDeleteDialog();
        }
      } catch (error) {
        this.showSnackBar("Error delete number: " + error, "red");
      } finally {
        this.deleteProgress = false;
      }
    },

    async exportNumbersToCsv() {
      try {
        this.exportProgress = true;
        const token = localStorage.getItem("token");

        const api = process.env.VUE_APP_BASE_URL + "/admin_api/export_numbers_csv";
        const res = await fetch(api, ApiServices.requestOptions("GET", "", token));

        if (res.status >= 400) {
          // השרת מחזיר JSON במקרה של שגיאה, לא קובץ
          let message = "שגיאה בייצוא הקובץ";
          try {
            const errorJson = await res.json();
            message = errorJson.message || errorJson || message;
          } catch (e) { /* התשובה לא JSON */ }
          this.showSnackBar(message, "red");
          return;
        }

        const blob = await res.blob();
        const fileName = this.getFileNameFromResponse(res) || `numbers_${new Date().toISOString().slice(0, 10)}.csv`;

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.showSnackBar("הקובץ יוצא בהצלחה", "green");
      } catch (error) {
        this.showSnackBar("Error export numbers: " + error, "red");
      } finally {
        this.exportProgress = false;
      }
    },

    getFileNameFromResponse(res) {
      const disposition = res.headers.get("Content-Disposition");
      if (!disposition) return null;
      const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      return match ? decodeURIComponent(match[1]) : null;
    },

    refreshDetails() {
      this.getNumbers();
    },

    // === events של סרבר פייגינג ===
    onPageChange(newPage) {
      this.currentPage = newPage;
      this.getNumbers();
    },

    onItemsPerPageChange(newItemsPerPage) {
      this.itemsPerPage = newItemsPerPage;
      this.getNumbers(true);
    },

    onSortChange() {
      this.debouncedGetNumbers(true);
    },

    handleSearchInput() {
      if (this.search) {
        this.debouncedGetNumbers(true);
      } else {
        this.getNumbers(true);
      }
    }
  },
  mounted() {
    this.lineNumber = Auth.getLineNumber();
    this.getNumbers();
    this.debouncedGetNumbers = _.debounce(this.getNumbers, 700);
  },
}
</script>


<style scoped>
.admin-container {
  font-family: 'Heebo', sans-serif;
}

.header-actions {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
}

.export-btn {
  background: #ffffff !important;
  color: #0d2c6d !important;
  border: 1px solid #0d2c6d;
  font-weight: 600;
  padding: 0 24px;
  height: 48px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.export-btn:hover {
  background: #0d2c6d !important;
  color: #ffffff !important;
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

.add-numbers-btn {
  background: linear-gradient(135deg, #ffb91a 0%, #ffd166 100%) !important;
  color: #0d2c6d !important;
  font-weight: 600;
  padding: 0 24px;
  height: 48px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.add-numbers-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

.search-card {
  margin-bottom: 24px;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.search-card:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1) !important;
}

.search-field {
  border-radius: 8px;
}

.numbers-count {
  display: flex;
  align-items: center;
  font-size: 16px;
  color: #0d2c6d;
}

.data-table-card {
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  margin-bottom: 40px;
}

.data-table-card:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1) !important;
}

.numbers-table {
  width: 100%;
}

.table-header-text {
  font-size: 16px;
  color: #0d2c6d;
}

.table-row {
  height: 60px !important;
  transition: background-color 0.2s ease;
}

.table-row:hover {
  background-color: rgba(13, 44, 109, 0.05) !important;
}

.table-cell {
  font-size: 14px;
  color: #333;
  text-align: center !important;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
}

.empty-text {
  font-size: 20px;
  font-weight: 600;
  color: #0d2c6d;
  margin-top: 16px;
}

.delete-dialog-card {
  border-radius: 12px;
  font-family: 'Heebo', sans-serif;
  padding: 8px;
}

.delete-dialog-title {
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
  color: #0d2c6d;
}

.delete-dialog-text {
  font-size: 16px;
  color: #333;
  padding-top: 12px;
}

.delete-dialog-actions {
  padding: 8px 16px 16px;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .admin-container {
    padding: 0 12px;
  }

  .header-actions {
    margin-bottom: 16px;
  }

  .table-header-text {
    font-size: 14px;
  }

  .table-cell {
    font-size: 13px;
  }
}

@media (max-width: 600px) {

  .add-numbers-btn,
  .export-btn {
    width: 100%;
  }

  .search-card,
  .data-table-card {
    border-radius: 8px;
  }

  .numbers-count {
    justify-content: center;
    margin-top: 8px;
  }
}


#table1.v-data-table>>>.v-data-table-header th {
  background-color: rgba(0, 0, 0, 0.116) !important;
  text-align: center !important;
  border-left: 1px solid white !important;
}
</style>